import { useState } from 'react';
import type { Movement } from '../../types';
import { MovementCard } from './MovementCard';
import { PlaneLanding, PlaneTakeoff, Settings2, SlidersHorizontal, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function MovementListView({ movements }: { movements: Movement[] }) {
  const arrivals = movements.filter(m => m.direction === 'inbound');
  const departures = movements.filter(m => m.direction === 'outbound');

  const [detailLevel, setDetailLevel] = useState<'L1' | 'L2' | 'L3'>('L2');

  return (
    <div className="h-full flex flex-col pt-2 pb-6 max-w-[1600px] mx-auto">
      
      {/* Global List Controls */}
      <div className="flex items-center justify-between mx-4 mb-6 bg-white rounded-xl p-2 border border-slate-200/60 shadow-sm backdrop-blur-xl bg-white/80 shrink-0">
          <div className="flex items-center gap-2 pl-2">
             <Search size={16} className="text-slate-400" />
             <Input 
                placeholder="Search tails, clients..." 
                className="w-[240px] border-none shadow-none h-8 text-sm placeholder:text-slate-400 focus-visible:ring-0 px-2 bg-transparent"
             />
          </div>

          <div className="flex items-center gap-4 pr-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-500 bg-slate-50 rounded-lg p-1 border border-slate-100">
               <Button 
                 variant="ghost" 
                 size="sm" 
                 className={`h-7 px-3 text-xs tracking-wide rounded ${detailLevel === 'L1' ? 'bg-white shadow-sm text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                 onClick={() => setDetailLevel('L1')}
               >
                 L1 Summary
               </Button>
               <Button 
                 variant="ghost" 
                 size="sm" 
                 className={`h-7 px-3 text-xs tracking-wide rounded ${detailLevel === 'L2' ? 'bg-white shadow-sm text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                 onClick={() => setDetailLevel('L2')}
               >
                 L2 Services
               </Button>
               <Button 
                 variant="ghost" 
                 size="sm" 
                 className={`h-7 px-3 text-xs tracking-wide rounded ${detailLevel === 'L3' ? 'bg-white shadow-sm text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                 onClick={() => setDetailLevel('L3')}
               >
                 L3 Detail
               </Button>
            </div>
            
            <div className="h-4 w-px bg-slate-200" />
            
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600">
               <SlidersHorizontal size={16} />
            </Button>
          </div>
      </div>

      <div className="flex-1 overflow-hidden grid grid-cols-2 gap-8 px-4">
        
        {/* Arrivals Column */}
        <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-slate-200/50 p-2 shadow-sm overflow-hidden isolate relative relative">
          
          <div className="flex items-center justify-between mb-4 sticky top-0 px-4 py-3 bg-slate-50/95 backdrop-blur z-10 border-b border-slate-200/50 rounded-t-xl -mx-2 -mt-2">
             <div className="flex items-center gap-3">
               <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100/80 text-blue-600">
                  <PlaneLanding size={18} strokeWidth={2.5} />
               </div>
               <h2 className="text-lg font-bold text-slate-800 tracking-tight">Arrivals</h2>
               <span className="flex items-center justify-center bg-slate-200/60 text-slate-600 text-xs font-bold rounded-full h-5 px-2">
                 {arrivals.length}
               </span>
             </div>
             
             <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                <Settings2 size={16} />
             </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-10 space-y-4 custom-scrollbar">
            {/* Rush Hour Divider Example */}
             <div className="flex items-center gap-4 py-2 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">08:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {arrivals.slice(0, 2).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}

            <div className="flex items-center gap-4 py-2 mt-4 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">10:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {arrivals.slice(2, 3).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}

              <div className="flex items-center gap-4 py-2 mt-4 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">14:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {arrivals.slice(3).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}
             
          </div>
          <div className="sticky bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none -mx-2"></div>
        </div>

        {/* Departures Column */}
        <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-slate-200/50 p-2 shadow-sm overflow-hidden isolate relative">
          
          <div className="flex items-center justify-between mb-4 sticky top-0 px-4 py-3 bg-slate-50/95 backdrop-blur z-10 border-b border-slate-200/50 rounded-t-xl -mx-2 -mt-2">
             <div className="flex items-center gap-3">
               <div className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-100/80 text-violet-600 relative">
                  <PlaneTakeoff size={18} strokeWidth={2.5} className="mr-0.5" />
               </div>
               <h2 className="text-lg font-bold text-slate-800 tracking-tight">Departures</h2>
               <span className="flex items-center justify-center bg-slate-200/60 text-slate-600 text-xs font-bold rounded-full h-5 px-2">
                 {departures.length}
               </span>
             </div>
             
             <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                <Settings2 size={16} />
             </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-10 space-y-4 custom-scrollbar">
             
             <div className="flex items-center gap-4 py-2 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">09:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {departures.slice(0, 1).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}

            <div className="flex items-center gap-4 py-2 mt-4 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">11:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {departures.slice(1, 2).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}

             <div className="flex items-center gap-4 py-2 mt-4 opacity-50">
                <div className="h-px bg-slate-300 flex-1"></div>
                <span className="text-xs font-bold tracking-widest text-slate-500 font-mono">14:00</span>
                <div className="h-px bg-slate-300 flex-1"></div>
             </div>

             {departures.slice(2).map(m => (
               <MovementCard key={m.id} movement={m} detailLevel={detailLevel} />
             ))}

             
          </div>
          <div className="sticky bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none -mx-2"></div>
        </div>

      </div>
    </div>
  );
}
